import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { articlesPart1 } from "@/mocks/articles-part1";
import { articlesPart2 } from "@/mocks/articles-part2";
import { articlesPart3 } from "@/mocks/articles-part3";
import { mainSiteUrl } from "@/mocks/categories";

const allArticles = [...articles, ...articlesPart1, ...articlesPart2, ...articlesPart3];

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "pl" || i18n.language === "es" || i18n.language === "fr" ? i18n.language : "en";

  const article = allArticles.find((a) => a.slug === slug) ?? allArticles[0];
  const content = article.content[lang] ?? article.content.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfanslink.pics";
  const pageUrl = `${siteUrl}/article/${article.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: content.title,
    description: content.metaDescription,
    image: article.cover,
    datePublished: article.updated,
    dateModified: article.updated,
    inLanguage: lang,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
    author: { "@type": "Organization", name: "USFansLink", url: siteUrl },
    publisher: {
      "@type": "Organization",
      name: "USFansLink",
      logo: {
        "@type": "ImageObject",
        url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg",
      },
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <main>
        <article className="mx-auto max-w-3xl px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">{t("nav.home")}</Link>
            <i className="ri-arrow-right-s-line text-base" />
            <span className="text-foreground-700 whitespace-nowrap">USFans Spreadsheet</span>
          </nav>

          <h1 className="mt-5 font-heading font-semibold text-3xl md:text-4xl leading-tight text-foreground-950">
            {content.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-line" />
              <span className="font-medium text-foreground-700">{t("common.author")}: USFansLink</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {t("common.updated")}: {article.updated}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" />
              {article.readTime} {t("common.readTime")}
            </span>
          </div>

          <div className="mt-8 rounded-lg bg-primary-100/60 border border-primary-200 p-5">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-700">{t("common.quickAnswer")}</span>
            <p className="mt-2 text-foreground-800 leading-relaxed">{content.quickAnswer}</p>
          </div>

          <div className="mt-8 prose-content">
            <p className="text-foreground-700 leading-relaxed text-base md:text-lg">{content.intro}</p>

            {content.sections.map((section, i) => (
              <section key={i} className="mt-8">
                <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="mt-4 text-foreground-700 leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <section className="mt-10">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">{t("common.faqShort")}</h2>
            <div className="mt-5 space-y-3">
              {content.faq.map((item, i) => (
                <details key={i} className="group rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                  <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-semibold text-foreground-900">{item.q}</span>
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:rotate-45 transition-transform shrink-0">
                      <i className="ri-add-line" />
                    </span>
                  </summary>
                  <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-2xl bg-background-100/70 border border-background-200 p-8 text-center">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">{t("common.relatedResources")}</h2>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                {t("common.backHome")}
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-foreground-950 font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-links-line" />
                {t("hero.ctaPrimary")}
              </a>
            </div>
          </section>
        </article>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </div>
  );
}